import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet } from '../../utils/backendApi';
import useTelemetry from './useTelemetry';
import { useAppLive } from './useAppLive';
import { deriveCommand, commandInspector, topologyColumns } from '../three/commandState';

// One app's command feed + telemetry, shared by the in-page command view
// (AppsModule → AppCommandView), the full-page app room and the fleet
// room's drill-in. Polls `/apps/:org/:app/command` every 30 s while the
// tab is visible; telemetry comes from the edge rollups or the simulator
// over the app (+ its UAT twin). `inspectorFor(sel)` builds the live card
// for a stage selection; `columns` are the request-path chips. `fixture`
// (a feed-shaped object) replaces the poll and puts telemetry on the
// simulator — the /_stages rooms.
//
// Two realtime overlays make the stage move between polls: the app doc
// (`app`, an onSnapshot in the caller) lends its `monitor` block, so the
// probe round trip fires the second the monitor writes it; and the live
// wire (useAppLive) streams requests + container vitals.
export default function useAppCommand({ orgId, appId, app = null, enabled = true, fixture = null }) {
  const [polled, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!enabled || !orgId || !appId || fixture) return undefined;
    let alive = true;
    const load = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const out = await apiGet(`/apps/${orgId}/${appId}/command`);
        if (alive) { setData(out); setErr(''); }
      } catch (e) {
        if (alive) setErr(e.message);
      }
    };
    load();
    const t = setInterval(load, 30000);
    document.addEventListener('visibilitychange', load);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', load); };
  }, [orgId, appId, enabled, fixture]);

  const mon = app?.monitor || null;
  const data = useMemo(() => {
    const base = fixture || polled;
    if (!base || !mon?.lastCheckAt || !base.plane || base.plane.unreachable) return base;
    if ((base.plane.monitor?.lastCheckAt || 0) >= mon.lastCheckAt) return base;
    return { ...base, plane: { ...base.plane, monitor: { ...(base.plane.monitor || {}), ...mon } }, surfaces: base.surfaces ? { ...base.surfaces, mcp: mon.mcp || base.surfaces.mcp } : base.surfaces };
  }, [fixture, polled, mon]);

  const state = useMemo(() => deriveCommand(data), [data]);
  const slug = app?.slug || state.viewer?.slug || null;
  const roster = useMemo(() => (slug ? [{ slug, twinSlug: state.twin?.exists ? `${slug}-uat` : null }, ...(state.twin?.exists ? [{ slug: `${slug}-uat`, envRole: 'uat' }] : [])] : []), [slug, state.twin?.exists]);
  const fetchReal = useCallback((w) => (fixture ? Promise.resolve(null) : apiGet(`/apps/${orgId}/telemetry?window=${w}&appId=${appId}`)), [orgId, appId, fixture]);
  const { telemetry, controls } = useTelemetry({ surface: 'app', roster, fetchReal, focusSlug: slug, enabled: enabled && !!data });
  const { live, feed } = useAppLive({ orgId, appId, enabled: enabled && !!data, fixture: fixture?.live || null });
  const inspectorFor = useCallback((sel) => commandInspector(state, telemetry, sel), [state, telemetry]);
  const columns = useMemo(() => (data ? topologyColumns(state, telemetry) : null), [data, state, telemetry]);
  return { data, err, state, slug, telemetry, controls, inspectorFor, columns, live, feed };
}
